<template>
  <div class="log-management">
    <div class="page-header">
      <h2>日志管理</h2>
      <div class="header-actions">
        <el-button 
          type="success" 
          icon="el-icon-download" 
          @click="exportOperationLog"
          :loading="exportingOperation">
          导出操作日志
        </el-button>
        <el-button 
          type="warning" 
          icon="el-icon-download" 
          @click="exportLoginLog"
          :loading="exportingLogin">
          导出登录日志
        </el-button>
      </div>
    </div>

    <!-- 标签页 -->
    <el-tabs v-model="activeTab" @tab-click="handleTabClick">
      <!-- 操作日志 -->
      <el-tab-pane label="操作日志" name="operation">
        <!-- 搜索表单 -->
        <el-form :inline="true" :model="operationSearchForm" class="search-form">
          <el-form-item label="用户名">
            <el-input v-model="operationSearchForm.user_name" placeholder="请输入用户名" clearable />
          </el-form-item>
          <el-form-item label="模块">
            <el-select v-model="operationSearchForm.module" placeholder="请选择模块" clearable>
              <el-option label="用户管理" value="user" />
              <el-option label="角色管理" value="role" />
              <el-option label="权限管理" value="permission" />
              <el-option label="部门管理" value="department" />
              <el-option label="字典管理" value="dict" />
              <el-option label="商品管理" value="goods" />
              <el-option label="分类管理" value="category" />
              <el-option label="店铺管理" value="shop" />
              <el-option label="店主管理" value="owner" />
            </el-select>
          </el-form-item>
          <el-form-item label="操作类型">
            <el-select v-model="operationSearchForm.operation" placeholder="请选择操作类型" clearable>
              <el-option label="创建" value="create" />
              <el-option label="更新" value="update" />
              <el-option label="删除" value="delete" />
              <el-option label="分配" value="assign" />
            </el-select>
          </el-form-item>
          <el-form-item label="请求方法">
            <el-select v-model="operationSearchForm.method" placeholder="请选择请求方法" clearable>
              <el-option label="GET" value="GET" />
              <el-option label="POST" value="POST" />
              <el-option label="PUT" value="PUT" />
              <el-option label="DELETE" value="DELETE" />
            </el-select>
          </el-form-item>
          <el-form-item label="IP地址">
            <el-input v-model="operationSearchForm.ip" placeholder="请输入IP地址" clearable />
          </el-form-item>
          <el-form-item label="状态">
            <el-select v-model="operationSearchForm.status" placeholder="请选择状态" clearable>
              <el-option label="成功" :value="1" />
              <el-option label="失败" :value="0" />
            </el-select>
          </el-form-item>
          <el-form-item label="时间范围">
            <el-date-picker
              v-model="operationTimeRange"
              type="datetimerange"
              range-separator="至"
              start-placeholder="开始时间"
              end-placeholder="结束时间"
              format="yyyy-MM-dd HH:mm:ss"
              value-format="yyyy-MM-dd HH:mm:ss"
            />
          </el-form-item>
          <el-form-item>
            <el-button type="primary" @click="searchOperationLog">搜索</el-button>
            <el-button @click="resetOperationSearch">重置</el-button>
            <el-button type="success" icon="el-icon-download" @click="exportOperationLog" :loading="exportingOperation">导出</el-button>
            <el-button type="danger" @click="clearOperationLog">清空日志</el-button>
          </el-form-item>
        </el-form>

        <!-- 操作日志表格 -->
        <el-table v-loading="operationLoading" :data="operationList" border style="width: 100%">
          <el-table-column prop="id" label="ID" width="80" />
          <el-table-column prop="user_name" label="用户名" width="120" />
          <el-table-column prop="module" label="模块" width="100">
            <template slot-scope="scope">
              <el-tag :type="getModuleTag(scope.row.module)">{{ getModuleText(scope.row.module) }}</el-tag>
            </template>
          </el-table-column>
          <el-table-column prop="operation" label="操作类型" width="100">
            <template slot-scope="scope">
              <el-tag :type="getOperationTag(scope.row.operation)">{{ getOperationText(scope.row.operation) }}</el-tag>
            </template>
          </el-table-column>
          <el-table-column prop="description" label="操作描述" min-width="200" show-overflow-tooltip />
          <el-table-column prop="method" label="请求方法" width="100">
            <template slot-scope="scope">
              <el-tag :type="getMethodTag(scope.row.method)" size="small">{{ scope.row.method }}</el-tag>
            </template>
          </el-table-column>
          <el-table-column prop="ip" label="IP地址" width="130" />
          <el-table-column prop="ip_address" label="IP归属地" width="150" show-overflow-tooltip />
          <el-table-column prop="execution_time" label="执行时间" width="100">
            <template slot-scope="scope">
              <span :class="getExecutionTimeClass(scope.row.execution_time)">
                {{ scope.row.execution_time }}ms
              </span>
            </template>
          </el-table-column>
          <el-table-column prop="status" label="状态" width="80">
            <template slot-scope="scope">
              <el-tag :type="scope.row.status === 1 ? 'success' : 'danger'" size="small">
                {{ scope.row.status === 1 ? '成功' : '失败' }}
              </el-tag>
            </template>
          </el-table-column>
          <el-table-column prop="create_time" label="操作时间" width="160" />
          <el-table-column label="操作" width="100" fixed="right">
            <template slot-scope="scope">
              <div class="table-operations">
                <el-button size="mini" type="text" @click="viewOperationDetail(scope.row)">查看</el-button>
              </div>
            </template>
          </el-table-column>
        </el-table>

        <!-- 分页 -->
        <div class="pagination">
          <el-pagination
            @size-change="handleOperationSizeChange"
            @current-change="handleOperationCurrentChange"
            :current-page="operationPagination.page"
            :page-sizes="[10, 20, 50, 100]"
            :page-size="operationPagination.limit"
            layout="total, sizes, prev, pager, next, jumper"
            :total="operationPagination.total"
          />
        </div>
      </el-tab-pane>

      <!-- 登录日志 -->
      <el-tab-pane label="登录日志" name="login">
        <!-- 搜索表单 -->
        <el-form :inline="true" :model="loginSearchForm" class="search-form">
          <el-form-item label="用户名">
            <el-input v-model="loginSearchForm.user_name" placeholder="请输入用户名" clearable />
          </el-form-item>
          <el-form-item label="登录类型">
            <el-select v-model="loginSearchForm.login_type" placeholder="请选择登录类型" clearable>
              <el-option label="密码登录" value="password" />
              <el-option label="手机验证码" value="sms" />
              <el-option label="第三方登录" value="oauth" />
            </el-select>
          </el-form-item>
          <el-form-item label="登录状态">
            <el-select v-model="loginSearchForm.status" placeholder="请选择登录状态" clearable>
              <el-option label="成功" :value="1" />
              <el-option label="失败" :value="0" />
            </el-select>
          </el-form-item>
          <el-form-item label="IP地址">
            <el-input v-model="loginSearchForm.ip" placeholder="请输入IP地址" clearable />
          </el-form-item>
          <el-form-item label="时间范围">
            <el-date-picker
              v-model="loginTimeRange"
              type="datetimerange"
              range-separator="至"
              start-placeholder="开始时间"
              end-placeholder="结束时间"
              format="yyyy-MM-dd HH:mm:ss"
              value-format="yyyy-MM-dd HH:mm:ss"
            />
          </el-form-item>
          <el-form-item>
            <el-button type="primary" @click="searchLoginLog">搜索</el-button>
            <el-button @click="resetLoginSearch">重置</el-button>
            <el-button type="success" icon="el-icon-download" @click="exportLoginLog" :loading="exportingLogin">导出</el-button>
            <el-button type="danger" @click="clearLoginLog">清空日志</el-button>
          </el-form-item>
        </el-form>

        <!-- 登录日志表格 -->
        <el-table v-loading="loginLoading" :data="loginList" border style="width: 100%">
          <el-table-column prop="id" label="ID" width="80" />
          <el-table-column prop="user_name" label="用户名" width="120" />
          <el-table-column prop="login_type" label="登录类型" width="120">
            <template slot-scope="scope">
              <el-tag :type="getLoginTypeTag(scope.row.login_type)" size="small">{{ getLoginTypeText(scope.row.login_type) }}</el-tag>
            </template>
          </el-table-column>
          <el-table-column prop="status" label="状态" width="80">
            <template slot-scope="scope">
              <el-tag :type="scope.row.status === 1 ? 'success' : 'danger'" size="small">
                {{ scope.row.status === 1 ? '成功' : '失败' }}
              </el-tag>
            </template>
          </el-table-column>
          <el-table-column prop="ip" label="IP地址" width="130" />
          <el-table-column prop="ip_address" label="IP归属地" width="180" show-overflow-tooltip />
          <el-table-column prop="user_agent" label="用户代理" min-width="200" show-overflow-tooltip />
          <el-table-column prop="error_msg" label="失败原因" width="150" show-overflow-tooltip />
          <el-table-column prop="create_time" label="登录时间" width="160" />
          <el-table-column label="操作" width="100" fixed="right">
            <template slot-scope="scope">
              <div class="table-operations">
                <el-button size="mini" type="text" @click="viewLoginDetail(scope.row)">查看</el-button>
              </div>
            </template>
          </el-table-column>
        </el-table>

        <!-- 分页 -->
        <div class="pagination">
          <el-pagination
            @size-change="handleLoginSizeChange"
            @current-change="handleLoginCurrentChange"
            :current-page="loginPagination.page"
            :page-sizes="[10, 20, 50, 100]"
            :page-size="loginPagination.limit"
            layout="total, sizes, prev, pager, next, jumper"
            :total="loginPagination.total"
          />
        </div>
      </el-tab-pane>
    </el-tabs>

    <!-- 操作日志详情对话框 -->
    <el-dialog title="操作日志详情" :visible.sync="operationDetailVisible" width="900px">
      <div v-if="operationDetail" class="log-detail">
        <!-- 基本信息 -->
        <div class="detail-section">
          <div class="section-title">基本信息</div>
          <div class="detail-table">
            <div class="detail-row">
              <div class="detail-label">日志ID</div>
              <div class="detail-value">{{ operationDetail.id }}</div>
            </div>
            <div class="detail-row">
              <div class="detail-label">用户ID</div>
              <div class="detail-value">{{ operationDetail.user_id }}</div>
            </div>
            <div class="detail-row">
              <div class="detail-label">用户名</div>
              <div class="detail-value">{{ operationDetail.user_name }}</div>
            </div>
            <div class="detail-row">
              <div class="detail-label">模块</div>
              <div class="detail-value">
                <el-tag :type="getModuleTag(operationDetail.module)">{{ getModuleText(operationDetail.module) }}</el-tag>
              </div>
            </div>
            <div class="detail-row">
              <div class="detail-label">操作类型</div>
              <div class="detail-value">
                <el-tag :type="getOperationTag(operationDetail.operation)">{{ getOperationText(operationDetail.operation) }}</el-tag>
              </div>
            </div>
            <div class="detail-row">
              <div class="detail-label">操作描述</div>
              <div class="detail-value">{{ operationDetail.description }}</div>
            </div>
            <div class="detail-row">
              <div class="detail-label">操作状态</div>
              <div class="detail-value">
                <el-tag :type="operationDetail.status === 1 ? 'success' : 'danger'">
                  {{ operationDetail.status === 1 ? '成功' : '失败' }}
                </el-tag>
              </div>
            </div>
            <div class="detail-row" v-if="operationDetail.error_msg">
              <div class="detail-label">错误信息</div>
              <div class="detail-value error-text">{{ operationDetail.error_msg }}</div>
            </div>
          </div>
        </div>

        <!-- 请求信息 -->
        <div class="detail-section">
          <div class="section-title">请求信息</div>
          <div class="detail-table">
            <div class="detail-row">
              <div class="detail-label">请求方法</div>
              <div class="detail-value">
                <el-tag :type="getMethodTag(operationDetail.method)" size="small">{{ operationDetail.method }}</el-tag>
              </div>
            </div>
            <div class="detail-row">
              <div class="detail-label">请求URL</div>
              <div class="detail-value">{{ operationDetail.request_url }}</div>
            </div>
            <div class="detail-row">
              <div class="detail-label">请求参数</div>
              <div class="detail-value">
                <pre class="json-pre">{{ formatJson(operationDetail.params) }}</pre>
              </div>
            </div>
            <div class="detail-row">
              <div class="detail-label">执行时间</div>
              <div class="detail-value">
                <span :class="getExecutionTimeClass(operationDetail.execution_time)">
                  {{ operationDetail.execution_time }}ms
                </span>
              </div>
            </div>
          </div>
        </div>

        <!-- 网络信息 -->
        <div class="detail-section">
          <div class="section-title">网络信息</div>
          <div class="detail-table">
            <div class="detail-row">
              <div class="detail-label">IP地址</div>
              <div class="detail-value">{{ operationDetail.ip }}</div>
            </div>
            <div class="detail-row">
              <div class="detail-label">IP归属地</div>
              <div class="detail-value">{{ operationDetail.ip_address || '-' }}</div>
            </div>
            <div class="detail-row">
              <div class="detail-label">用户代理</div>
              <div class="detail-value">{{ operationDetail.user_agent }}</div>
            </div>
            <div class="detail-row">
              <div class="detail-label">操作时间</div>
              <div class="detail-value">{{ operationDetail.create_time }}</div>
            </div>
          </div>
        </div>
      </div>
      <div slot="footer" class="dialog-footer">
        <el-button @click="operationDetailVisible = false">关闭</el-button>
      </div>
    </el-dialog>

    <!-- 登录日志详情对话框 -->
    <el-dialog title="登录日志详情" :visible.sync="loginDetailVisible" width="900px">
      <div v-if="loginDetail" class="log-detail">
        <!-- 基本信息 -->
        <div class="detail-section">
          <div class="section-title">基本信息</div>
          <div class="detail-table">
            <div class="detail-row">
              <div class="detail-label">日志ID</div>
              <div class="detail-value">{{ loginDetail.id }}</div>
            </div>
            <div class="detail-row">
              <div class="detail-label">用户ID</div>
              <div class="detail-value">{{ loginDetail.user_id || '-' }}</div>
            </div>
            <div class="detail-row">
              <div class="detail-label">用户名</div>
              <div class="detail-value">{{ loginDetail.user_name }}</div>
            </div>
            <div class="detail-row">
              <div class="detail-label">登录类型</div>
              <div class="detail-value">
                <el-tag :type="getLoginTypeTag(loginDetail.login_type)" size="small">
                  {{ getLoginTypeText(loginDetail.login_type) }}
                </el-tag>
              </div>
            </div>
            <div class="detail-row">
              <div class="detail-label">登录状态</div>
              <div class="detail-value">
                <el-tag :type="loginDetail.status === 1 ? 'success' : 'danger'">
                  {{ loginDetail.status === 1 ? '成功' : '失败' }}
                </el-tag>
              </div>
            </div>
            <div class="detail-row" v-if="loginDetail.error_msg">
              <div class="detail-label">失败原因</div>
              <div class="detail-value error-text">{{ loginDetail.error_msg }}</div>
            </div>
            <div class="detail-row">
              <div class="detail-label">登录时间</div>
              <div class="detail-value">{{ loginDetail.create_time }}</div>
            </div>
          </div>
        </div>

        <!-- 网络信息 -->
        <div class="detail-section">
          <div class="section-title">网络信息</div>
          <div class="detail-table">
            <div class="detail-row">
              <div class="detail-label">IP地址</div>
              <div class="detail-value">{{ loginDetail.ip }}</div>
            </div>
            <div class="detail-row">
              <div class="detail-label">IP归属地</div>
              <div class="detail-value">{{ loginDetail.ip_address || '-' }}</div>
            </div>
            <div class="detail-row">
              <div class="detail-label">用户代理</div>
              <div class="detail-value">{{ loginDetail.user_agent }}</div>
            </div>
          </div>
        </div>
      </div>
      <div slot="footer" class="dialog-footer">
        <el-button @click="loginDetailVisible = false">关闭</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { getOperationLogList, getLoginLogList, clearOperationLog, clearLoginLog } from '../../api/system'
import { downloadFile } from '@/utils/request'

export default {
  name: 'LogManagement',
  data() {
    return {
      activeTab: 'operation',
      operationTimeRange: [],
      loginTimeRange: [],
      exportingOperation: false,
      exportingLogin: false,
      
      // 操作日志相关
      operationLoading: false,
      operationList: [],
      operationSearchForm: {
        user_name: '',
        module: '',
        operation: '',
        method: '',
        ip: '',
        status: '',
        start_time: '',
        end_time: ''
      },
      operationPagination: {
        page: 1,
        limit: 20,
        total: 0
      },
      operationDetailVisible: false,
      operationDetail: {},
      
      // 登录日志相关
      loginLoading: false,
      loginList: [],
      loginSearchForm: {
        user_name: '',
        login_type: '',
        status: '',
        ip: '',
        start_time: '',
        end_time: ''
      },
      loginPagination: {
        page: 1,
        limit: 20,
        total: 0
      },
      loginDetailVisible: false,
      loginDetail: {}
    }
  },
  created() {
    this.fetchOperationLogList()
    this.fetchLoginLogList()
  },
  methods: {
    // 标签页切换
    handleTabClick() {
      if (this.activeTab === 'operation') {
        this.fetchOperationLogList()
      } else {
        this.fetchLoginLogList()
      }
    },
    
    // 操作日志相关方法
    async fetchOperationLogList() {
      this.operationLoading = true
      try {
        const params = {
          ...this.operationSearchForm,
          page: this.operationPagination.page,
          limit: this.operationPagination.limit
        }
        const response = await getOperationLogList(params)
        if (response.code === 200) {
          this.operationList = response.data.list
          this.operationPagination.total = response.data.total
        }
      } catch (error) {
        this.$message.error('获取操作日志列表失败')
      } finally {
        this.operationLoading = false
      }
    },
    searchOperationLog() {
      if (this.operationTimeRange && this.operationTimeRange.length === 2) {
        this.operationSearchForm.start_time = this.operationTimeRange[0]
        this.operationSearchForm.end_time = this.operationTimeRange[1]
      } else {
        this.operationSearchForm.start_time = ''
        this.operationSearchForm.end_time = ''
      }
      this.operationPagination.page = 1
      this.fetchOperationLogList()
    },
    resetOperationSearch() {
      this.operationSearchForm = {
        user_name: '',
        module: '',
        operation: '',
        method: '',
        ip: '',
        status: '',
        start_time: '',
        end_time: ''
      }
      this.operationTimeRange = []
      this.operationPagination.page = 1
      this.fetchOperationLogList()
    },
    handleOperationSizeChange(val) {
      this.operationPagination.limit = val
      this.fetchOperationLogList()
    },
    handleOperationCurrentChange(val) {
      this.operationPagination.page = val
      this.fetchOperationLogList()
    },
    viewOperationDetail(row) {
      this.operationDetail = row
      this.operationDetailVisible = true
    },
    async clearOperationLog() {
      try {
        const { value: days } = await this.$prompt('请输入要保留的天数', '清空操作日志', {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          inputValue: 30,
          inputValidator: (value) => {
            if (!value || value < 1) {
              return '请输入大于0的天数'
            }
            return true
          }
        })
        
        const response = await clearOperationLog({ days: parseInt(days) })
        if (response.code === 200) {
          this.$message.success(`已清空${days}天前的操作日志，共清理${response.data.cleared_count}条记录`)
          this.fetchOperationLogList()
        } else {
          this.$message.error(response.msg || '清空操作日志失败')
        }
      } catch (error) {
        if (error !== 'cancel') {
          this.$message.error('清空操作日志失败')
        }
      }
    },
    exportOperationLog() {
      try {
        this.exportingOperation = true

        // 构建查询参数
        const params = {}

        // 添加搜索条件
        if (this.operationSearchForm.user_name) {
          params.user_name = this.operationSearchForm.user_name
        }
        if (this.operationSearchForm.module) {
          params.module = this.operationSearchForm.module
        }
        if (this.operationSearchForm.operation) {
          params.operation = this.operationSearchForm.operation
        }
        if (this.operationSearchForm.method) {
          params.method = this.operationSearchForm.method
        }
        if (this.operationSearchForm.ip) {
          params.ip = this.operationSearchForm.ip
        }
        if (this.operationSearchForm.status !== '') {
          params.status = this.operationSearchForm.status
        }
        if (this.operationSearchForm.start_time) {
          params.start_time = this.operationSearchForm.start_time
        }
        if (this.operationSearchForm.end_time) {
          params.end_time = this.operationSearchForm.end_time
        }

        // 显示提示信息
        this.$message({
          message: '正在导出操作日志，请稍候...',
          type: 'info',
          duration: 2000
        })

        // 走带 token 的请求下载（window.open 带不了 Authorization 头）
        downloadFile('/system.log_controller/exportOperationLog', params, '操作日志导出.csv').then(() => {
          this.$message({
            message: '导出成功，请检查浏览器下载',
            type: 'success',
            duration: 3000
          })
        }).catch(() => {
          this.$message.error('导出失败，请稍后重试')
        }).finally(() => {
          this.exportingOperation = false
        })

      } catch (error) {
        console.error('导出操作日志失败:', error)
        this.$message.error('导出失败，请稍后重试')
        this.exportingOperation = false
      }
    },
    
    // 登录日志相关方法
    async fetchLoginLogList() {
      this.loginLoading = true
      try {
        const params = {
          ...this.loginSearchForm,
          page: this.loginPagination.page,
          limit: this.loginPagination.limit
        }
        const response = await getLoginLogList(params)
        if (response.code === 200) {
          this.loginList = response.data.list
          this.loginPagination.total = response.data.total
        }
      } catch (error) {
        this.$message.error('获取登录日志列表失败')
      } finally {
        this.loginLoading = false
      }
    },
    searchLoginLog() {
      if (this.loginTimeRange && this.loginTimeRange.length === 2) {
        this.loginSearchForm.start_time = this.loginTimeRange[0]
        this.loginSearchForm.end_time = this.loginTimeRange[1]
      } else {
        this.loginSearchForm.start_time = ''
        this.loginSearchForm.end_time = ''
      }
      this.loginPagination.page = 1
      this.fetchLoginLogList()
    },
    resetLoginSearch() {
      this.loginSearchForm = {
        user_name: '',
        login_type: '',
        status: '',
        ip: '',
        start_time: '',
        end_time: ''
      }
      this.loginTimeRange = []
      this.loginPagination.page = 1
      this.fetchLoginLogList()
    },
    handleLoginSizeChange(val) {
      this.loginPagination.limit = val
      this.fetchLoginLogList()
    },
    handleLoginCurrentChange(val) {
      this.loginPagination.page = val
      this.fetchLoginLogList()
    },
    viewLoginDetail(row) {
      this.loginDetail = row
      this.loginDetailVisible = true
    },
    async clearLoginLog() {
      try {
        const { value: days } = await this.$prompt('请输入要保留的天数', '清空登录日志', {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          inputValue: 90,
          inputValidator: (value) => {
            if (!value || value < 1) {
              return '请输入大于0的天数'
            }
            return true
          }
        })
        
        const response = await clearLoginLog({ days: parseInt(days) })
        if (response.code === 200) {
          this.$message.success(`已清空${days}天前的登录日志，共清理${response.data.cleared_count}条记录`)
          this.fetchLoginLogList()
        } else {
          this.$message.error(response.msg || '清空登录日志失败')
        }
      } catch (error) {
        if (error !== 'cancel') {
          this.$message.error('清空登录日志失败')
        }
      }
    },
    exportLoginLog() {
      try {
        this.exportingLogin = true

        // 构建查询参数
        const params = {}

        // 添加搜索条件
        if (this.loginSearchForm.user_name) {
          params.user_name = this.loginSearchForm.user_name
        }
        if (this.loginSearchForm.login_type) {
          params.login_type = this.loginSearchForm.login_type
        }
        if (this.loginSearchForm.status !== '') {
          params.status = this.loginSearchForm.status
        }
        if (this.loginSearchForm.ip) {
          params.ip = this.loginSearchForm.ip
        }
        if (this.loginSearchForm.start_time) {
          params.start_time = this.loginSearchForm.start_time
        }
        if (this.loginSearchForm.end_time) {
          params.end_time = this.loginSearchForm.end_time
        }

        // 显示提示信息
        this.$message({
          message: '正在导出登录日志，请稍候...',
          type: 'info',
          duration: 2000
        })

        // 走带 token 的请求下载（window.open 带不了 Authorization 头）
        downloadFile('/system.log_controller/exportLoginLog', params, '登录日志导出.csv').then(() => {
          this.$message({
            message: '导出成功，请检查浏览器下载',
            type: 'success',
            duration: 3000
          })
        }).catch(() => {
          this.$message.error('导出失败，请稍后重试')
        }).finally(() => {
          this.exportingLogin = false
        })

      } catch (error) {
        console.error('导出登录日志失败:', error)
        this.$message.error('导出失败，请稍后重试')
        this.exportingLogin = false
      }
    },
    
    // 工具方法
    getMethodTag(method) {
      const tags = {
        GET: 'success',
        POST: 'primary',
        PUT: 'warning',
        DELETE: 'danger'
      }
      return tags[method] || 'info'
    },
    getModuleTag(module) {
      const tags = {
        user: 'primary',
        role: 'success',
        permission: 'warning',
        department: 'info',
        dict: 'danger',
        goods: 'primary',
        category: 'success',
        shop: 'warning',
        owner: 'info'
      }
      return tags[module] || 'info'
    },
    getModuleText(module) {
      const texts = {
        user: '用户',
        role: '角色',
        permission: '权限',
        department: '部门',
        dict: '字典',
        goods: '商品',
        category: '分类',
        shop: '店铺',
        owner: '店主'
      }
      return texts[module] || module
    },
    getOperationTag(operation) {
      const tags = {
        create: 'success',
        update: 'primary',
        delete: 'danger',
        assign: 'warning',
        login: 'info',
        logout: 'info'
      }
      return tags[operation] || 'info'
    },
    getOperationText(operation) {
      const texts = {
        create: '创建',
        update: '更新',
        delete: '删除',
        assign: '分配',
        login: '登录',
        logout: '退出'
      }
      return texts[operation] || operation
    },
    getExecutionTimeClass(time) {
      if (time < 100) return 'time-fast'
      if (time < 500) return 'time-normal'
      if (time < 1000) return 'time-slow'
      return 'time-very-slow'
    },
    getLoginTypeTag(type) {
      const tags = {
        password: 'primary',
        sms: 'success',
        oauth: 'warning'
      }
      return tags[type] || 'info'
    },
    getLoginTypeText(type) {
      const texts = {
        password: '密码登录',
        sms: '手机验证码',
        oauth: '第三方登录'
      }
      return texts[type] || type
    },
    formatJson(json) {
      if (!json) return '-'
      try {
        if (typeof json === 'string') {
          return JSON.stringify(JSON.parse(json), null, 2)
        }
        return JSON.stringify(json, null, 2)
      } catch {
        return json
      }
    }
  }
}
</script>

<style scoped>
.log-management {
  padding: 20px;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.page-header h2 {
  margin: 0;
  font-size: 24px;
  color: #303133;
}

.header-actions {
  display: flex;
  gap: 12px;
}

.header-actions .el-button {
  font-weight: 500;
}

.search-form {
  background: #f5f5f5;
  padding: 20px;
  border-radius: 4px;
  margin-bottom: 20px;
}

.pagination {
  margin-top: 20px;
  text-align: right;
}

.table-operations {
  display: flex;
  gap: 4px;
  justify-content: center;
  flex-wrap: nowrap;
}

.table-operations .el-button--text {
  padding: 2px 6px;
  font-size: 12px;
  border: none;
  background: none;
  white-space: nowrap;
  min-width: unset;
}

/* 执行时间颜色 */
.time-fast {
  color: #67c23a;
  font-weight: 500;
}

.time-normal {
  color: #409eff;
  font-weight: 500;
}

.time-slow {
  color: #e6a23c;
  font-weight: 500;
}

.time-very-slow {
  color: #f56c6c;
  font-weight: 600;
}

/* 详情对话框样式 */
.log-detail {
  padding: 10px 0;
}

.detail-section {
  margin-bottom: 30px;
}

.detail-section:last-child {
  margin-bottom: 0;
}

.section-title {
  font-size: 16px;
  font-weight: 600;
  color: #303133;
  margin-bottom: 15px;
  padding-left: 10px;
  border-left: 4px solid #409EFF;
  line-height: 20px;
}

.detail-table {
  border: 1px solid #EBEEF5;
  border-radius: 4px;
  overflow: hidden;
  background-color: #fff;
}

.detail-row {
  display: flex;
  border-bottom: 1px solid #EBEEF5;
  min-height: 50px;
  align-items: center;
}

.detail-row:last-child {
  border-bottom: none;
}

.detail-label {
  font-weight: 500;
  color: #606266;
  width: 150px;
  text-align: right;
  padding: 15px 20px;
  background-color: #FAFAFA;
  border-right: 1px solid #EBEEF5;
  flex-shrink: 0;
  box-sizing: border-box;
}

.detail-value {
  color: #303133;
  word-break: break-word;
  padding: 15px 20px;
  flex: 1;
  box-sizing: border-box;
}

.error-text {
  color: #f56c6c;
  font-weight: 500;
}

.json-pre {
  white-space: pre-wrap;
  word-wrap: break-word;
  max-height: 300px;
  overflow-y: auto;
  background-color: #f5f7fa;
  padding: 10px;
  border-radius: 4px;
  font-size: 12px;
  line-height: 1.6;
  margin: 0;
  font-family: 'Courier New', Courier, monospace;
}

.dialog-footer {
  text-align: right;
}
</style>